'use client';
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import * as React from 'react';
import PropTypes from 'prop-types';
import { HTMLElementType, refType } from '@mui/utils';
import { getMenuUtilityClass } from "./menuClasses.js";
import { useMenu } from "../useMenu/index.js";
import { MenuProvider } from "../useMenu/MenuProvider.js";
import { unstable_composeClasses as composeClasses } from "../composeClasses/index.js";
import { Unstable_Popup as Popup } from "../Unstable_Popup/index.js";
import { useSlotProps } from "../utils/useSlotProps.js";
import { useClassNamesOverride } from "../utils/ClassNameConfigurator.js";
import { ListActionTypes } from "../useList/index.js";
import { jsx as _jsx } from "react/jsx-runtime";
function useUtilityClasses(ownerState) {
    var open = ownerState.open;
    var slots = {
        root: [
            'root',
            open && 'expanded'
        ],
        listbox: [
            'listbox',
            open && 'expanded'
        ]
    };
    return composeClasses(slots, useClassNamesOverride(getMenuUtilityClass));
}
/**
 *
 * Demos:
 *
 * - [Menu](https://mui.com/base-ui/react-menu/)
 *
 * API:
 *
 * - [Menu API](https://mui.com/base-ui/react-menu/components-api/#menu)
 */ var Menu = /*#__PURE__*/ React.forwardRef(function Menu(props, forwardedRef) {
    var actions = props.actions, anchorProp = props.anchor, children = props.children, onItemsChange = props.onItemsChange, _props_slotProps = props.slotProps, slotProps = _props_slotProps === void 0 ? {} : _props_slotProps, _props_slots = props.slots, slots = _props_slots === void 0 ? {} : _props_slots, other = _object_without_properties(props, [
        "actions",
        "anchor",
        "children",
        "onItemsChange",
        "slotProps",
        "slots"
    ]);
    var _useMenu = useMenu({
        onItemsChange: onItemsChange,
        componentName: 'Menu'
    }), contextValue = _useMenu.contextValue, getListboxProps = _useMenu.getListboxProps, dispatch = _useMenu.dispatch, open = _useMenu.open, triggerElement = _useMenu.triggerElement;
    var anchor = anchorProp !== null && anchorProp !== void 0 ? anchorProp : triggerElement;
    React.useImperativeHandle(actions, function() {
        return {
            dispatch: dispatch,
            resetHighlight: function() {
                return dispatch({
                    type: ListActionTypes.resetHighlight,
                    event: null
                });
            }
        };
    }, [
        dispatch
    ]);
    var ownerState = _object_spread_props(_object_spread({}, props), {
        open: open
    });
    var classes = useUtilityClasses(ownerState);
    var _slots_root;
    var Root = (_slots_root = slots.root) !== null && _slots_root !== void 0 ? _slots_root : 'div';
    var rootProps = useSlotProps({
        elementType: Root,
        externalSlotProps: slotProps.root,
        externalForwardedProps: other,
        additionalProps: {
            ref: forwardedRef,
            role: undefined
        },
        className: classes.root,
        ownerState: ownerState
    });
    var _slots_listbox;
    var Listbox = (_slots_listbox = slots.listbox) !== null && _slots_listbox !== void 0 ? _slots_listbox : 'ul';
    var listboxProps = useSlotProps({
        elementType: Listbox,
        getSlotProps: getListboxProps,
        externalSlotProps: slotProps.listbox,
        className: classes.listbox,
        ownerState: ownerState
    });
    if (open === true && anchor == null) {
        return /*#__PURE__*/ _jsx(Root, _object_spread_props(_object_spread({}, rootProps), {
            children: /*#__PURE__*/ _jsx(Listbox, _object_spread_props(_object_spread({}, listboxProps), {
                children: /*#__PURE__*/ _jsx(MenuProvider, {
                    value: contextValue,
                    children: children
                })
            }))
        }));
    }
    return /*#__PURE__*/ _jsx(Popup, _object_spread_props(_object_spread({
        keepMounted: true
    }, rootProps), {
        open: open,
        anchor: anchor,
        slots: {
            root: Root
        },
        children: /*#__PURE__*/ _jsx(Listbox, _object_spread_props(_object_spread({}, listboxProps), {
            children: /*#__PURE__*/ _jsx(MenuProvider, {
                value: contextValue,
                children: children
            })
        }))
    }));
});
process.env.NODE_ENV !== "production" ? Menu.propTypes = {
    // ┌────────────────────────────── Warning ──────────────────────────────┐
    // │ These PropTypes are generated from the TypeScript type definitions. │
    // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
    // └─────────────────────────────────────────────────────────────────────┘
    /**
   * A ref with imperative actions that can be performed on the menu.
   */ actions: refType,
    /**
   * The element based on which the menu is positioned.
   */ anchor: PropTypes /* @typescript-to-proptypes-ignore */ .oneOfType([
        HTMLElementType,
        PropTypes.object,
        PropTypes.func
    ]),
    /**
   * @ignore
   */ children: PropTypes.node,
    /**
   * @ignore
   */ className: PropTypes.string,
    /**
   * Function called when the items displayed in the menu change.
   */ onItemsChange: PropTypes.func,
    /**
   * The props used for each slot inside the Menu.
   * @default {}
   */ slotProps: PropTypes.shape({
        listbox: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object
        ]),
        root: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object
        ])
    }),
    /**
   * The components used for each slot inside the Menu.
   * Either a string to use a HTML element or a component.
   * @default {}
   */ slots: PropTypes.shape({
        listbox: PropTypes.elementType,
        root: PropTypes.elementType
    })
} : void 0;
export { Menu };
